import { useEffect, useState } from 'react'

import { Button, ButtonGroup, useIsTabletOrMobile } from '@avantstay/backoffice-core'

import { DateInputs } from './components'
import {
  desktopViewOptions,
  mobileAndTabletViewOptions,
  viewComponents,
} from './DatePicker.constants'
import * as S from './DatePicker.styles'
import {
  DateInputsProps,
  DatePickerOpenRangePayload,
  DatePickerPayload,
  DatePickerProps,
  View,
  ViewComponentProps,
} from './DatePicker.types'

export const DatePicker = ({
  value,
  metadata = { view: 'custom' },
  views,
  presets,
  allowOpenRange,
  onChange,
  onCancel,
}: DatePickerProps) => {
  const [internalValue, setInternalValue] = useState(value)
  const [internalMetadata, setInternalMetadata] = useState(metadata)

  const isTabletOrMobile = useIsTabletOrMobile()

  const handleChangeView = (newView: View) => {
    setInternalMetadata(prev => ({ ...prev, view: newView }))
  }

  const handleInputChange: DateInputsProps['onChange'] = newValue => {
    setInternalValue(newValue)
    setInternalMetadata(prev => ({ ...prev, preset: undefined }))
  }

  const handleChange: ViewComponentProps['onChange'] = newPayload => {
    setInternalValue(newPayload.value)
    setInternalMetadata(prev => ({ ...prev, ...newPayload.metadata }))
  }

  const handleApply = () => {
    if (!internalValue) return onChange(undefined)

    if (allowOpenRange) {
      onChange({ value: internalValue, metadata: internalMetadata } as DatePickerOpenRangePayload)
    } else {
      onChange({ value: internalValue, metadata: internalMetadata } as DatePickerPayload)
    }
  }

  const viewOptions = (isTabletOrMobile ? mobileAndTabletViewOptions : desktopViewOptions).filter(
    option => !views || views.includes(option.value),
  )
  const ViewComponent = viewComponents[internalMetadata.view]

  useEffect(() => {
    // Calendar view is only visible on desktop
    if (isTabletOrMobile && internalMetadata.view === 'calendar') {
      setInternalMetadata({ view: 'custom', preset: undefined })
    }
  }, [isTabletOrMobile, internalMetadata])

  return (
    <S.Container>
      <S.PaddingContainer>
        <DateInputs
          key={`${internalValue?.startDate?.toDateString()}_${internalValue?.endDate?.toDateString()}`}
          value={internalValue}
          allowOpenRange={allowOpenRange}
          onChange={handleInputChange}
        />

        {viewOptions.length > 1 ? (
          <ButtonGroup
            fullWidth
            options={viewOptions.map(option => ({
              ...option,
              checked: option.value === internalMetadata.view,
            }))}
            onClick={handleChangeView}
          />
        ) : null}

        <S.ViewContainer>
          <ViewComponent
            value={internalValue}
            preset={internalMetadata.preset}
            presets={presets}
            onChange={handleChange}
          />
        </S.ViewContainer>
      </S.PaddingContainer>

      <S.Actions>
        {onCancel ? <Button.Default onClick={onCancel}>Discard</Button.Default> : null}
        <Button.Primary onClick={handleApply}>Apply filters</Button.Primary>
      </S.Actions>
    </S.Container>
  )
}
